import type {Key, Collection, Node} from 'react-stately'
import {getAllDescendantKeys, findSiblingParentKeys, hasChildNodes, getParentKey} from './util'

function removeParentAndDescendants<T>(
    selection: Set<Key>,
    parentKey: Key,
    collection: Collection<Node<T>>,
): void {
    selection.delete(parentKey)
    const descendantKeys = getAllDescendantKeys(parentKey, collection)
    descendantKeys.forEach((descKey) => selection.delete(descKey))
}

function deselectSiblingParents<T>(
    selection: Set<Key>,
    selectedKey: Key,
    collection: Collection<Node<T>>,
): void {
    const siblingParentKeys = findSiblingParentKeys(selectedKey, collection)

    for (const siblingKey of siblingParentKeys) {
        removeParentAndDescendants(selection, siblingKey, collection)
    }
}

function deselectOtherParents<T>(
    selection: Set<Key>,
    leafParentKey: Key,
    currentSelectionSet: Set<Key>,
    collection: Collection<Node<T>>,
): void {
    for (const selectedKey of currentSelectionSet) {
        const selectedNode = collection.getItem(selectedKey)
        if (!selectedNode) {
            continue
        }

        const selectedKeyParent = selectedNode.parentKey
        const isSelectedParent = selectedNode.hasChildNodes && !selectedKeyParent

        if (isSelectedParent && selectedKey !== leafParentKey) {
            removeParentAndDescendants(selection, selectedKey, collection)
        } else if (selectedKeyParent && selectedKeyParent !== leafParentKey) {
            selection.delete(selectedKey)
        }
    }
}

function deselectAllParents<T>(
    selection: Set<Key>,
    currentSelectionSet: Set<Key>,
    collection: Collection<Node<T>>,
): void {
    for (const selectedKey of currentSelectionSet) {
        const selectedNode = collection.getItem(selectedKey)
        if (!selectedNode) {
            continue
        }

        const selectedKeyParent = selectedNode.parentKey
        const isSelectedParent = selectedNode.hasChildNodes && !selectedKeyParent

        if (isSelectedParent) {
            removeParentAndDescendants(selection, selectedKey, collection)
        } else if (selectedKeyParent) {
            selection.delete(selectedKey)
        }
    }
}

export function applyGranularSelectionRules<T>(
    modifiedSet: Set<Key>,
    addedKeys: Key[],
    currentSelectionSet: Set<Key>,
    collection: Collection<Node<T>>,
): void {
    for (const addedKey of addedKeys) {
        const node = collection.getItem(addedKey)
        const hasChildren = hasChildNodes(addedKey, collection)
        const hasParent = node?.parentKey != null

        if (hasChildren && !hasParent) {
            deselectSiblingParents(modifiedSet, addedKey, collection)
        } else {
            const leafParentKey = getParentKey(addedKey, collection)

            if (leafParentKey) {
                deselectOtherParents(modifiedSet, leafParentKey, currentSelectionSet, collection)
            } else {
                deselectAllParents(modifiedSet, currentSelectionSet, collection)
            }
        }
    }
}
